import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

// Ensure session cookies are sent with every Axios request
axios.defaults.withCredentials = true;

const OnboardingContext = createContext(null);
const STORAGE_KEY = 'studyfolio.user.v2';
const LEGACY_KEY = 'studyfolio.user.v1';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Remember-me storage: "keep me signed in" → localStorage (persists across
// restarts); unchecked → sessionStorage (dies with the tab/browser session).
function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}
function writeStored(u, remember) {
    try {
        if (remember === false) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(u));
            localStorage.removeItem(STORAGE_KEY);
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
            sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {}
}
function clearStored() {
    try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(LEGACY_KEY);
    } catch {}
}

// Any user missing these fields must be re-prompted through onboarding.
function isFullyOnboarded(u) {
    return !!(u && u.time_commitment && Array.isArray(u.focus_areas) && u.focus_areas.length > 0 && u.primary_objective);
}

export function OnboardingProvider({ children }) {
    const [user, setUser] = useState(() => {
        const stored = readStored();
        if (!stored) clearStored();
        return stored;
    });
    const [phase, setPhase] = useState(() => (isFullyOnboarded(user) ? 'inside' : 'closed'));
    const [authResumed, setAuthResumed] = useState(false);

    useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  if (token) {
    // Save token or pass in auth headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Optionally clean up the URL parameter
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}, []);

    useEffect(() => {
        if (user) writeStored(user, user.remember !== false);
    }, [user]);

    // Bootstrap: if a Google session cookie exists, hydrate from the server.
    useEffect(() => {
        if (window.location.hash?.includes('session_id=')) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
                if (cancelled) return;
                const srv = res.data;
                setUser((prev) => ({ ...(prev || {}), ...srv, authed: true }));
                if (isFullyOnboarded(srv)) setPhase('inside');
                else setPhase('closed'); // re-prompt for profile
            } catch {
                /* not authenticated */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    function completeOnboarding(data) {
        const remember = data.keepSignedIn !== false;
        const merged = {
            ...(user || {}), ...data, remember,
            is_guest: !!data.is_guest,
            onboarding_completed: true,
            completedAt: new Date().toISOString(),
        };
        setUser(merged);

        if (merged.is_guest) return merged; // guests: local-only profile

        const payload = {
            name: data.name,
            class_level: data.class_level,
            primary_exam_track: data.primary_exam_track,
            additional_national_exams: data.additional_national_exams || [],
            selected_subjects: data.focus_areas || [],
            time_commitment: data.time_commitment,
            primary_objective: data.primary_objective,
            plan_tier: data.plan_tier || 'free',
            plan_price_inr: data.plan_price_inr || 0,
            plan_credits: data.plan_credits ?? 20,
        };
        axios
            .post(`${API}/onboarding/complete`, payload, { withCredentials: true })
            .catch(() => {
                // Unauthenticated local identity path (email/phone w/o cookie)
                axios
                    .post(`${API}/onboarding/local`, {
                        email: data.email || merged.email,
                        phone: data.phone || merged.phone,
                        name: data.name,
                        education: data.class_level,
                        goal: data.goal,
                        board_class: data.class_level,
                        time_commitment: data.time_commitment,
                        focus_areas: data.focus_areas,
                        primary_objective: data.primary_objective,
                    })
                    .catch(() => {});
            });
        return merged;
    }

    function setAuthUser(srvUser) {
        setUser((prev) => ({ ...(prev || {}), ...srvUser, authed: true, is_guest: false }));
    }

    async function reset() {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch {}
        clearStored();
        setUser(null);
        setPhase('closed');
    }

    return (
        <OnboardingContext.Provider
            value={{
                user,
                phase,
                setPhase,
                completeOnboarding,
                setAuthUser,
                reset,
                authResumed,
                setAuthResumed,
                isFullyOnboarded: () => isFullyOnboarded(user),
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
}

export function useOnboarding() {
    const ctx = useContext(OnboardingContext);
    if (!ctx) throw new Error('useOnboarding must be inside OnboardingProvider');
    return ctx;
}
